/**
 * Input component with Dashboard3 styling
 * Integrates with React Hook Form
 *
 * Supports both register-based and Controller-based usage.
 * Password type automatically includes show/hide toggle.
 *
 * @example
 * ```tsx
 * // Register-based (most common)
 * <Input
 *   {...register('email')}
 *   type="email"
 *   placeholder="Enter your email"
 * />
 *
 * // Controller-based (for complex validation)
 * <Controller
 *   name="email"
 *   control={control}
 *   render={({ field }) => (
 *     <Input {...field} type="email" placeholder="Enter your email" />
 *   )}
 * />
 *
 * // Password with auto toggle
 * <Input {...register('password')} type="password" />
 * ```
 */

"use client";

import {
  Input as ChakraInput,
  type InputProps as ChakraInputProps,
  Icon,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { forwardRef, useCallback, useState } from "react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface InputProps extends ChakraInputProps {
  /**
   * Input type
   */
  type?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ type = "text", ...props }, ref) => {
    const theme = useThemeTokens();
    const [show, setShow] = useState(false);
    const isPassword = type === "password";

    const handleToggle = useCallback(() => {
      setShow((prev) => !prev);
    }, []);

    const inputProps = {
      ref,
      type: isPassword ? (show ? "text" : "password") : type,
      fontSize: "sm",
      fontWeight: "500",
      borderRadius: "8px",
      borderWidth: "1px",
      borderColor: theme.borderGlass.default,
      bg: "transparent",
      color: theme.text.primary,
      h: "44px",
      px: 4,
      _placeholder: {
        color: theme.text.tertiary,
        fontWeight: "400",
      },
      _focus: {
        borderColor: theme.accent.primary,
        boxShadow: theme.focusRing,
        bg: `transparent !important`,
        color: `${theme.text.primary} !important`,
      },
      _invalid: {
        borderColor: theme.accent.primary,
      },
      _autofill: {
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: theme.text.primary,
        boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
        transition: "background-color 600000s 0s, color 600000s 0s",
        caretColor: theme.text.primary,
      },
      sx: {
        "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
          WebkitBackgroundClip: "text !important",
          backgroundClip: "text !important",
          WebkitTextFillColor: `${theme.text.primary} !important`,
          boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
          transition: "background-color 600000s 0s, color 600000s 0s !important",
          caretColor: `${theme.text.primary} !important`,
        },
        "&:-internal-autofill-selected": {
          WebkitBackgroundClip: "text !important",
          backgroundClip: "text !important",
          WebkitTextFillColor: `${theme.text.primary} !important`,
          boxShadow: `0 0 0 1000px rgba(0, 0, 0, 0) inset !important`,
        },
        "&:focus": {
          bg: `transparent !important`,
          color: `${theme.text.primary} !important`,
          boxShadow: `${theme.focusRing} !important`,
        },
        "&:focus:-webkit-autofill": {
          boxShadow: `${theme.focusRing} !important`,
        },
        // Hover only on devices that support hover
        "@media (hover: hover)": {
          "&:hover:not(:focus):not(:disabled)": {
            borderColor: theme.accent.primaryBorder,
            bg: "transparent",
          },
        },
      },
      transition: "all 0.18s ease-out",
      ...props,
    };

    if (isPassword) {
      return (
        <InputGroup>
          <ChakraInput {...inputProps} />
          <InputRightElement h="100%" pr={3}>
            <Icon
              as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
              color={theme.text.tertiary}
              onClick={handleToggle}
              cursor="pointer"
              fontSize="18px"
              transition="color 0.18s ease-out"
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    color: theme.text.secondary,
                  },
                },
              }}
            />
          </InputRightElement>
        </InputGroup>
      );
    }

    return <ChakraInput {...inputProps} />;
  },
);

Input.displayName = "Input";

